// --- external imports ------

const DEFAULT_RETRIES = 2;
const DEFAULT_BASE_DELAY_MS = 800;

function delay(milliseconds) {
	return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Runs a request again while `shouldRetry` accepts the failure it threw.
 *
 * Auth requests are retried in place rather than surfaced immediately, so a user
 * system that is briefly unreachable costs the player a few seconds under the
 * loading cursor instead of a valid session. Only failures that say nothing about
 * the player's credentials are retried — the caller decides that through
 * `shouldRetry`, and everything else is rethrown on the first attempt.
 */
async function runWithRetry(request, options) {
	const retries = options?.retries ?? DEFAULT_RETRIES;
	const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
	const shouldRetry = options?.shouldRetry ?? (() => true);

	let attempt = 0;

	for (;;) {
		try {
			return await request();
		}
		catch (error) {
			attempt += 1;

			if (attempt > retries || !shouldRetry(error)) {
				throw error;
			}

			// Jittered: every client that was talking to the user system when it
			// went down fails at the same moment, so a fixed backoff has all of
			// them come back at the same moment too — right as it is trying to
			// come up. The spread is half to one and a half of the delay.
			const backoffMs = baseDelayMs * (2 ** (attempt - 1));
			await delay(backoffMs * (0.5 + Math.random()));
		}
	}
}

export default runWithRetry;
